import { ID, MinimalRelationship } from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';
import { withRoles } from '../utils/with-roles';

export type CreateRelationshipsOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  relationships: MinimalRelationship[];
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (collectionRef: NoInfer<TCollectionRef>, id: string) => TDocRef;
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  writeBatch?: (db: NoInfer<TDb>) => {
    set: (ref: NoInfer<TDocRef>, data: Record<string, unknown>) => void;
    commit: () => Promise<void>;
  };
  onWriteError: (error: unknown) => void;
};

const BATCH_SIZE = 500;

export const buildRelationshipDocument = <TFieldValue>(
  relationship: MinimalRelationship,
  userId: ID,
  serverTimestamp: () => TFieldValue,
): Record<string, unknown> => ({
  ...withRoles(relationship, userId),
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

export const createRelationships = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb = FirestoreInstance,
>(
  options: CreateRelationshipsOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TDb
  >,
): Promise<void> => {
  const {
    relationships,
    userId,
    collection,
    doc,
    setDoc,
    db,
    serverTimestamp,
    writeBatch,
    onWriteError,
  } = options;

  if (!relationships.length) {
    return;
  }

  const relationshipsCollection = collection(
    db,
    FirestoreCollections.Relationships,
  );

  if (!writeBatch) {
    relationships.forEach((relationship) =>
      detachWrite(
        setDoc(
          doc(relationshipsCollection, relationship.id),
          buildRelationshipDocument(relationship, userId, serverTimestamp),
        ),
        onWriteError,
      ),
    );
    return;
  }

  const batchCount = Math.ceil(relationships.length / BATCH_SIZE);
  const batches = Array.from({ length: batchCount }, () => writeBatch(db));

  relationships.forEach((relationship, index) => {
    const batchIndex = Math.floor(index / BATCH_SIZE);
    batches[batchIndex].set(
      doc(relationshipsCollection, relationship.id),
      buildRelationshipDocument(relationship, userId, serverTimestamp),
    );
  });

  batches.forEach((batch) => detachWrite(batch.commit(), onWriteError));
};
